import { XCircleIcon } from "@phosphor-icons/react/dist/csr/XCircle";
import { useMemo } from "react";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { getErrorMessage } from "@/core/api/errors";
import { PayBreakdownPaneShell } from "@/payroll/pay-breakdown/pay-breakdown-pane-shell";
import {
  AMOUNT_COLUMN,
  LINES_COLUMN,
  SelectCol,
  TECHNICIAN_COLUMN,
} from "./cols";
import { StAdjustmentPushConfirmationDialog } from "./push-confirmation-dialog";
import { PushPayExcludedLines } from "./pushpay-excluded-lines";
import { StAdjustmentsHeader } from "./st-adjustments-header";
import { StAdjustmentsRollupTable } from "./st-adjustments-rollup-table";
import { useStAdjustmentsSection } from "./st-adjustments-section";

// The ServiceTitan adjustments surface: a by-technician rollup you can push from,
// with each row drilling into its own lines.
//
// A tab rather than a dialog because this is a long-running, auditable action.
// The header and toast messages report push state while the user keeps working,
// and the URL gives the rollup a stable place to return to.
//
// Scale-first: the table is the rollup (bounded by technician count), never the
// line list (thousands). Pushing a selection or everything pending is a server-
// side request by technician id, so the client never enumerates line keys; only
// the drill-down loads individual lines, one technician at a time.
//
// This renders the rollup only. The push action lives in the page header and the
// per-technician detail in the page sidebar — both read the same section
// provider, so all three stay in one React tree (see `st-adjustments-section`).
export const StAdjustmentsView = () => {
  const {
    isLoading,
    summaryError,
    totals,
    warnings,
    hasRows,
    push,
    selection,
    pendingIntent,
    setPendingIntent,
    confirmPush,
  } = useStAdjustmentsSection();

  const columns = useMemo(
    () => [
      SelectCol({
        disabled: push.isBusy,
        selectedTechnicianIds: selection.selectedTechnicianIds,
        allPushableSelected: selection.allPushableSelected,
        somePushableSelected: selection.somePushableSelected,
        pushableCount: selection.pushableGroups.length,
        onToggleAll: selection.toggleAll,
        onToggle: selection.toggleTechnician,
      }),
      TECHNICIAN_COLUMN,
      AMOUNT_COLUMN,
      LINES_COLUMN,
    ],
    [
      push.isBusy,
      selection.selectedTechnicianIds,
      selection.allPushableSelected,
      selection.somePushableSelected,
      selection.pushableGroups.length,
      selection.toggleAll,
      selection.toggleTechnician,
    ],
  );

  return (
    <>
      <PayBreakdownPaneShell fillHeight gap={6} className="h-full">
        {summaryError && !isLoading && (
          <Alert color="danger">
            <XCircleIcon />
            <AlertDescription>
              <Text>{getErrorMessage(summaryError)}</Text>
            </AlertDescription>
          </Alert>
        )}

        {isLoading && (
          <Spinner layout="centered" className="h-40" tone="primary" />
        )}

        {!isLoading && !summaryError && totals && totals.count === 0 && (
          <Empty className={emptyStateVariants({ layout: "inline" })}>
            <EmptyHeader>
              <EmptyDescription>
                No commissions or spiffs are tagged for PushPay on this run.
              </EmptyDescription>
              <PushPayExcludedLines warnings={warnings} />
            </EmptyHeader>
          </Empty>
        )}

        {/* `totals` is redundant with `hasRows` — but that boolean crosses a
            context boundary, so it no longer narrows the value it was derived
            from. */}
        {hasRows && totals && (
          <StAdjustmentsRollupTable columns={columns}>
            <StAdjustmentsHeader />
          </StAdjustmentsRollupTable>
        )}
      </PayBreakdownPaneShell>

      <StAdjustmentPushConfirmationDialog
        open={!!pendingIntent}
        intent={pendingIntent}
        isPushing={push.isEnqueuing}
        onOpenChange={(open) => {
          if (!open) setPendingIntent(null);
        }}
        onConfirm={confirmPush}
      />
    </>
  );
};
